import { Divider, Form, Typography } from 'antd';
import styled from 'styled-components';
import { APP_SHELL_THEME } from '@/styles/appShellTheme';

const { Text } = Typography;

// Consistent 8px-based spacing scale used throughout this drawer (8 / 12 / 16px),
// matching the rhythm already used elsewhere in the form.
/**
 * The send bar, pinned to the bottom of the viewport.
 *
 * The form runs past the fold, so a footer in normal flow put the only action
 * — and any statement of what is still missing — somewhere the reader had to
 * go looking for. Pinned, the next step is always in view.
 */
export const FooterBar = styled.div`
  position: sticky;
  bottom: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  width: 100%;
  margin-top: 28px;
  padding: 12px 0;
  background: ${APP_SHELL_THEME.pageBg};
  border-top: 1px solid ${APP_SHELL_THEME.panelBorder};
`;

/** What is still needed before the IO can be sent, beside the button that
 *  sends it -- so the remaining work is legible without scrolling to find it. */
export const FooterStatus = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  font-size: 12.5px;
  color: ${APP_SHELL_THEME.textMuted};
`;

export const PreviewFrame = styled.iframe`
  width: 100%;
  height: 100%;
  min-height: 80vh;
  border: none;
  /* The embedded PDF viewer paints its own dark chrome around a landscape page.
     A white ground keeps the document reading as paper rather than leaving a
     black band beside it. */
  background: #fff;
`;

export const AlertSpacer = styled.div<{ $pullUp?: boolean }>`
  margin-bottom: 16px;
  ${({ $pullUp }) => $pullUp && 'margin-top: -8px;'}
`;

export const SectionLabel = styled(Text)`
  text-transform: uppercase;
  font-size: 12px;
  letter-spacing: 0.4px;
`;

export const SectionDivider = styled(Divider)`
  margin: 8px 0 16px;
`;

export const NameFieldsRow = styled.div`
  display: flex;
  gap: 12px;
`;

export const HalfWidthFormItem = styled(Form.Item)`
  flex: 1;
  min-width: 0;
`;

export const NoBottomMarginFormItem = styled(Form.Item)`
  margin-bottom: 0;
`;

/** Form beside the rendered document, so settings and result are seen together. */
export const SplitPane = styled.div`
  display: grid;
  /* The form needs only enough width for its fields; the rest goes to the
     document, which is landscape A4 and unreadable when squeezed. */
  grid-template-columns: minmax(320px, 380px) 1fr;
  gap: 24px;
  align-items: start;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

export const FormPane = styled.div`
  min-width: 0;
`;

export const DocumentPane = styled.div<{ $hasDocument: boolean }>`
  min-width: 0;
  /* Fill the viewport below the page header rather than a fixed fraction of
     it, so the landscape page is legible without scrolling the pane. */
  height: calc(100vh - 220px);
  min-height: 560px;
  position: sticky;
  top: 16px;
  /* White only once there is a document to be paper for. Empty, a white
     rectangle across half a dark screen reads as a failed load rather than a
     pane waiting for a template. */
  background: ${({ $hasDocument }) => ($hasDocument ? '#fff' : `rgba(${APP_SHELL_THEME.overlayRgb}, 0.03)`)};
  border: 1px solid
    ${({ $hasDocument }) => ($hasDocument ? `rgba(${APP_SHELL_THEME.overlayRgb}, 0.12)` : APP_SHELL_THEME.panelBorder)};
  border-radius: 6px;
  overflow: hidden;
  transition: background 160ms ease;
`;

/**
 * What the document pane says before a template is chosen.
 *
 * Centred on the pane's own dark surface with the step named plainly, so the
 * space reads as waiting for an input rather than as a blank page that failed
 * to render.
 */
export const PreviewPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  height: 100%;
  padding: 32px;
  text-align: center;
  color: ${APP_SHELL_THEME.textMuted};

  > .anticon {
    font-size: 28px;
    opacity: 0.45;
  }
`;

export const PlaceholderTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${APP_SHELL_THEME.text};
`;

export const PlaceholderHint = styled.div`
  font-size: 12.5px;
  max-width: 34ch;
  line-height: 1.5;
`;

/** Label text plus its info icon, kept on one line and vertically centred. */
export const LabelWithHint = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;

  .anticon {
    opacity: 0.55;
    font-size: 13px;
    cursor: help;
    transition: opacity 0.15s ease;
  }

  .anticon:hover,
  .anticon:focus-visible {
    opacity: 1;
  }
`;

/**
 * The "an IO already exists" card.
 *
 * Its own block rather than an Alert with a control stuffed into the
 * description: the existing IO is a piece of state with an action attached, so
 * it reads as a small record — version, status, date — with one button, not as
 * a paragraph of warning prose.
 */
/** Tag and undo, kept together at the end of the banner. */
export const ExistingIoActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

export const ExistingIoCard = styled.div<{ $signed: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  margin-bottom: 16px;
  border-radius: 6px;
  border: 1px solid ${({ $signed }) => ($signed ? 'rgba(250,173,20,0.35)' : 'rgba(22,119,255,0.3)')};
  background: ${({ $signed }) => ($signed ? 'rgba(250,173,20,0.08)' : 'rgba(22,119,255,0.06)')};

  > .anticon {
    font-size: 16px;
    flex-shrink: 0;
    color: ${({ $signed }) => ($signed ? '#faad14' : '#1677ff')};
  }
`;

export const ExistingIoText = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const ExistingIoTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
`;

export const ExistingIoMeta = styled.span`
  font-size: 12px;
  opacity: 0.65;
`;

/**
 * The internal-amendment choice, below the existing-IO card.
 *
 * Quieter than that card on purpose: it modifies the replacement rather than
 * being a second decision of equal weight, and it only appears once replacing
 * has been chosen.
 */
export const InternalOnlyCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: -8px 0 16px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px dashed ${APP_SHELL_THEME.metricCardBorder};
  background: ${APP_SHELL_THEME.metricCardBg};
`;
